import React, { useState, useMemo } from 'react';
import { Search, ChevronDown, ChevronUp, Mail, MessageCircle, Users, GraduationCap, Briefcase, Globe, CreditCard, Settings, Phone, BookOpen, Clock, CheckCircle, AlertCircle } from 'lucide-react';
import Banner from './Banner';

const faqData = [
    {
        id: 1,
        category: 'general',
        question: 'What is Techmentee?',
        answer: 'Techmentee is a career acceleration platform offering: IT training, resume building, and job placement assistance—especially tailored for international students, OPT/CPT candidates, H-1B holders, and professionals looking to break into or transition within the U.S. tech industry.',
        icon: <Users className="w-5 h-5" />
    },
    {
        id: 2,
        category: 'general',
        question: 'Who can join Techmentee?',
        answer: 'Our programs are open to: Recent graduates (especially STEM OPT), H-1B visa holders, International students on OPT/CPT, Green card holders and U.S. citizens seeking IT job placement.',
        icon: <Users className="w-5 h-5" />
    },
    {
        id: 3,
        category: 'training',
        question: 'What kind of training do you provide?',
        answer: 'We offer training in high-demand IT domains such as: Business Analysis, Data Analysis, Quality Assurance (QA), Scrum/Agile, AWS/Cloud Fundamentals, Java/Full Stack Development, and other client-demand-based custom programs.',
        icon: <GraduationCap className="w-5 h-5" />
    },
    {
        id: 4,
        category: 'training',
        question: 'Is your training live or recorded?',
        answer: 'We offer both live instructor-led sessions and recorded modules. You\'ll also get access to resources, mock interviews, and real-world project work.',
        icon: <BookOpen className="w-5 h-5" />
    },
    {
        id: 5,
        category: 'training',
        question: 'Do I need prior IT experience to join?',
        answer: 'Not necessarily. Some courses are beginner-friendly, while others may require basic technical knowledge. We\'ll assess your profile during the onboarding process.',
        icon: <CheckCircle className="w-5 h-5" />
    },
    {
        id: 6,
        category: 'placement',
        question: 'What\'s included in the job placement service?',
        answer: 'Resume and LinkedIn profile optimization, Weekly job application support, Recruiter network outreach, Interview preparation and mock sessions, One-on-one mentorship and progress tracking.',
        icon: <Briefcase className="w-5 h-5" />
    },
    {
        id: 7,
        category: 'placement',
        question: 'Do you guarantee a job?',
        answer: 'While we do not guarantee placement, our proven methods, recruiter connections, and job marketing support significantly increase your chances of securing a role.',
        icon: <AlertCircle className="w-5 h-5" />
    },
    {
        id: 8,
        category: 'placement',
        question: 'How long does it take to get placed?',
        answer: 'Placement timelines vary by individual and market demand but typically range from 4 to 12 weeks after completing training and profile setup.',
        icon: <Clock className="w-5 h-5" />
    },
    {
        id: 9,
        category: 'visa',
        question: 'Do you help with visa sponsorship?',
        answer: 'We work with recruiters and companies who may sponsor H-1B or other work visas, but Techmentee itself is not a staffing company and does not provide sponsorship directly.',
        icon: <Globe className="w-5 h-5" />
    },
    {
        id: 10,
        category: 'visa',
        question: 'Can I join if I\'m on OPT/CPT or H-1B?',
        answer: 'Yes. We specialize in supporting candidates on OPT, CPT, H-1B, and other work authorizations. Many of our clients are international professionals.',
        icon: <Globe className="w-5 h-5" />
    },
    {
        id: 11,
        category: 'payment',
        question: 'Is there a fee to join Techmentee?',
        answer: 'Yes. We offer different pricing models based on the program: Training-only packages, Job placement packages, All-in-one bundles. Custom pricing may be available depending on your situation.',
        icon: <CreditCard className="w-5 h-5" />
    },
    {
        id: 12,
        category: 'payment',
        question: 'Do you offer refunds?',
        answer: 'Our refund policy depends on the specific program you enroll in. Please contact support for more details.',
        icon: <CreditCard className="w-5 h-5" />
    },
    {
        id: 13,
        category: 'technical',
        question: 'I can\'t log into my account. What should I do?',
        answer: 'Try resetting your password using the "Forgot Password" option. If you\'re still locked out, contact us at support@techmentee.com.',
        icon: <Settings className="w-5 h-5" />
    }
];
const HelpFaq = () => {
    const [searchTerm, setSearchTerm] = useState('');
    const [activeCategory, setActiveCategory] = useState('all');
    const [expandedItems, setExpandedItems] = useState(new Set());
    

    const categories = [
        { id: 'all', name: 'All Categories', icon: <BookOpen className="w-4 h-4" />, color: 'bg-blue-500' },
        { id: 'general', name: 'General', icon: <Users className="w-4 h-4" />, color: 'bg-green-500' },
        { id: 'training', name: 'Training Program', icon: <GraduationCap className="w-4 h-4" />, color: 'bg-purple-500' },
        { id: 'placement', name: 'Job Placement', icon: <Briefcase className="w-4 h-4" />, color: 'bg-orange-500' },
        { id: 'visa', name: 'Immigration & Visa', icon: <Globe className="w-4 h-4" />, color: 'bg-indigo-500' },
        { id: 'payment', name: 'Fees & Payments', icon: <CreditCard className="w-4 h-4" />, color: 'bg-red-500' },
        { id: 'technical', name: 'Technical Support', icon: <Settings className="w-4 h-4" />, color: 'bg-gray-500' }
    ];

    const filteredFaqs = useMemo(() => {
        return faqData.filter(faq => {
            const matchesSearch = faq.question.toLowerCase().includes(searchTerm.toLowerCase()) ||
                faq.answer.toLowerCase().includes(searchTerm.toLowerCase());
            const matchesCategory = activeCategory === 'all' || faq.category === activeCategory;
            return matchesSearch && matchesCategory;
        });
    }, [searchTerm, activeCategory]);

    const toggleExpanded = (id) => {
        const newExpanded = new Set(expandedItems);
        if (newExpanded.has(id)) {
            newExpanded.delete(id);
        } else {
            newExpanded.add(id);
        }
        setExpandedItems(newExpanded);
    };

    const getCurrentCategoryColor = () => {
        const category = categories.find(cat => cat.id === activeCategory);
        return category?.color || 'bg-blue-500';
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100">
            <Banner pageName="HELP & FAQs" breadcrummbs="Home / Help & Faqs" />
            {/* Header */}
            <div className="bg-white shadow-lg border-b border-gray-200">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                    <div className="text-center">
                        <h1 className="text-4xl font-bold text-gray-900 mb-2">
                            Help & <span className="text-blue-600">FAQ</span>
                        </h1>
                        <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                            Welcome to the Techmentee Help Center. Find answers to the most commonly asked questions about our services, application process, and support options.
                        </p>
                    </div>
                </div>
            </div>

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
                    {/* Categories Sidebar */}
                    <div className="lg:col-span-1">
                        <div className="bg-white rounded-2xl shadow-xl p-6 sticky top-8">
                            <h2 className="text-xl font-bold text-gray-900 mb-4">Categories</h2>
                            <div className="space-y-2">
                                {categories.map(category => (
                                    <button
                                        key={category.id}
                                        onClick={() => setActiveCategory(category.id)}
                                        className={`w-full flex items-center px-4 py-3 rounded-xl transition-all duration-200 ${activeCategory === category.id
                                            ? `${category.color} text-white shadow-lg`
                                            : 'text-gray-700 hover:bg-gray-100'
                                            }`}
                                    >
                                        {category.icon}
                                        <span className="ml-3 font-medium">{category.name}</span>
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* FAQ Content */}
                    <div className="lg:col-span-3">
                        <div className="bg-white rounded-2xl shadow-xl">
                            <div className={`${getCurrentCategoryColor()} text-white p-6 rounded-t-2xl`}>
                                <h2 className="text-2xl font-bold">
                                    {categories.find(cat => cat.id === activeCategory)?.name || 'All Categories'}
                                </h2>
                                <p className="text-white/90 mt-1">
                                    {filteredFaqs.length} question{filteredFaqs.length !== 1 ? 's' : ''} found
                                </p>
                            </div>

                            <div className="p-6">
                                {filteredFaqs.length === 0 ? (
                                    <div className="text-center py-12">
                                        <AlertCircle className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                                        <h3 className="text-xl font-semibold text-gray-900 mb-2">No results found</h3>
                                        <p className="text-gray-600">
                                            Try adjusting your search terms or browse different categories.
                                        </p>
                                    </div>
                                ) : (
                                    <div className="space-y-4">
                                        {filteredFaqs.map((faq, index) => (
                                            <div
                                                key={faq.id}
                                                className="border border-gray-200 rounded-xl overflow-hidden hover:shadow-md transition-shadow duration-200"
                                            >
                                                <button
                                                    onClick={() => toggleExpanded(faq.id)}
                                                    className="w-full flex items-center justify-between p-6 text-left hover:bg-gray-50 transition-colors duration-200"
                                                >
                                                    <div className="flex items-center flex-1">
                                                        <div className={`${categories.find(cat => cat.id === faq.category)?.color} p-2 rounded-lg mr-4`}>
                                                            {faq.icon}
                                                        </div>
                                                        <h3 className="text-lg font-semibold text-gray-900 flex-1">
                                                            {faq.question}
                                                        </h3>
                                                    </div>
                                                    {expandedItems.has(faq.id) ? (
                                                        <ChevronUp className="w-5 h-5 text-gray-500" />
                                                    ) : (
                                                        <ChevronDown className="w-5 h-5 text-gray-500" />
                                                    )}
                                                </button>

                                                {expandedItems.has(faq.id) && (
                                                    <div className="px-6 pb-6">
                                                        <div className="ml-16 text-gray-700 leading-relaxed">
                                                            {faq.answer}
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Contact Section */}
                        {/* <div className="mt-8 bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl shadow-xl text-white p-8">
                            <div className="text-center">
                                <h2 className="text-2xl font-bold mb-4">Still need help?</h2>
                                <p className="text-blue-100 mb-6 max-w-2xl mx-auto">
                                    If you couldn't find the answer you're looking for, don't hesitate to reach out to our support team. We're here to help you succeed!
                                </p>
                                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                                    <button className="bg-white text-blue-600 px-6 py-3 rounded-xl font-semibold hover:bg-blue-50 transition-colors duration-200 flex items-center justify-center">
                                        <Mail className="w-5 h-5 mr-2" />
                                        Email Support
                                    </button>
                                    <button className="bg-blue-700 text-white px-6 py-3 rounded-xl font-semibold hover:bg-blue-800 transition-colors duration-200 flex items-center justify-center">
                                        <MessageCircle className="w-5 h-5 mr-2" />
                                        Start Live Chat
                                    </button>
                                </div>
                            </div>
                        </div> */}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default HelpFaq;